import { useMemo } from 'react';
import type {
  ChatChannelResponse,
  ChatChannelMemberResponse,
  ChatMemberPermissions,
} from '@/lib/api';
// Type-only import — erased at compile time, so this does NOT put the
// code-split MessageInput chunk back on the static-reachability graph
// (see chat/index.ts for why that must never happen).
import type { MessageInputPermissions } from '../components/MessageInput';

/**
 * Effective channel-level composer permissions for the current member.
 *
 * Lives in one place because ChatPage and the embedded guild-page pane
 * (EmbeddedChannelChat) both render MessageInput, and MessageInput defaults
 * every permission to allowed when the prop is omitted — a second copy of the
 * resolution rules (or a surface that skips them) silently shows an enabled
 * composer in a read-only channel.
 *
 * Rules: channel admins (owner/admin role) bypass all member restrictions;
 * individual member permissions override channel defaults; unset keys default
 * to allowed.
 */
export function resolveChannelMessagePermissions(
  channel: ChatChannelResponse | null,
  member: ChatChannelMemberResponse | null,
): MessageInputPermissions {
  const isChannelAdmin = member?.role === 'owner' || member?.role === 'admin';

  function resolve(key: keyof ChatMemberPermissions): boolean {
    if (isChannelAdmin) return true;
    const individual = member?.permissions?.[key];
    if (individual !== undefined) return individual !== false;
    const def = channel?.defaultPermissions?.[key];
    return def !== false;
  }

  return {
    canSendMessages: resolve('sendMessages'),
    canSendImages: resolve('sendImages'),
    canSendVideos: resolve('sendVideos'),
    canSendAudio: resolve('sendAudio'),
    canSendFiles: resolve('sendFiles'),
    canSendLinks: resolve('sendLinks'),
  };
}

/** Memoized `resolveChannelMessagePermissions` for use in render. */
export function useChannelMessagePermissions(
  channel: ChatChannelResponse | null,
  member: ChatChannelMemberResponse | null,
): MessageInputPermissions {
  return useMemo(() => resolveChannelMessagePermissions(channel, member), [channel, member]);
}
